import { useNavigate } from 'react-router-dom'
import { useStore } from 'react-redux'
import { useAppSelector } from '@/store/store-hooks'
import type { RootState } from '@/store/store'
import { selectActiveMatterId } from '@/matter/store/selectors'
import { buildConversationPath } from '@/chat-common/conversation-navigation/util/conversation-path-builders'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { DraftEntryPoint } from '@/analytics/enums/draft.enums'
import { draftNavigationState } from '@/analytics/utils/artifact-editor-entry-analytics'
import { selectMatterArtifactVersionById } from '@/matter-artifacts/store/selectors'
import { kRouteSegments } from '@/routing/paths'
import { kDraftSearchParam } from '@/drafting/workspace/hooks/useDraftWorkspace'

type NavigateToDraftInChatArgs = {
  artifactVersionId: string
  conversationId: string
  draftEntryPoint: DraftEntryPoint
  /** The draft's owning matter artifact, for callers holding drafts the store doesn't. */
  matterArtifactId?: string
}

/**
 * Returns a function that navigates to a draft in its source conversation.
 *
 * Matter artifact drafts use `draft` to open the drafting workspace.
 *
 * Emits `DraftEntryPointClicked` (observe-only) as the draft re-entry signal. It reads the owning
 * artifact id, which is stable across edits unlike the version id held by the carousel or chip.
 * Fire-and-forget; it never blocks navigation.
 */
export function useNavigateToDraftInChat() {
  const navigate = useNavigate()
  const store = useStore<RootState>()
  const { trackEvent } = useAnalytics()
  const matterId = useAppSelector((state: RootState) => selectActiveMatterId(state))

  return ({ artifactVersionId, conversationId, draftEntryPoint, matterArtifactId }: NavigateToDraftInChatArgs) => {
    if (!matterId) return

    const state = store.getState()
    const owningMatterArtifactId = matterArtifactId ?? selectMatterArtifactVersionById(state, artifactVersionId)?.matter_artifact_id
    if (owningMatterArtifactId) {
      trackEvent(AnalyticsEvent.DraftEntryPointClicked, { artifactId: owningMatterArtifactId, artifactVersionId, conversationId, matterId, draftEntryPoint })
    }

    // The param opens the chat with this draft in the draft view; the state scrolls the chat to the draft's artifact button.
    const path = buildConversationPath(matterId, conversationId, kRouteSegments.agentV2)
    navigate(`${path}?${kDraftSearchParam}=${artifactVersionId}`, {
      state: draftNavigationState(draftEntryPoint, { scrollToMatterArtifactVersionId: artifactVersionId }),
    })
  }
}
